import React, { ReactNode, useEffect } from 'react'
import { useState } from 'react'
import styled from 'styled-components'
import { useUserState } from '~/atoms/UserState'
import Text, { FontStyle } from '~/components/core/Text'
import {
  ResponsivePage,
  useTogglePanel,
} from '~/components/layout/ResponsivePage'
import { EmptyDetailView } from '~/components/shared/EmptyDetailView'
import { LoadingSpinner } from '~/components/shared/LoadingSpinner'
import { light } from '~/constants/theme'
import { TranscriptionView } from '~/pages/CallAnalytics/Transcription/TranscriptionView'
import {
  useGetCallTranscript,
  useGetTranscribedCalls,
} from '~/queries/callAnalyticsQueries'
import { useGetCall } from '~/queries/callQueries'
import { CallTranscript } from '~/types/CallTranscript'

import { CallLog, CallLogInstance } from '@mangovoice/types'

import { CallLogsList } from './CallLogs/CallLogsList'

const LoadingOrNoticeWrapper = styled.div`
  display: flex;
  flex: 1;
  -webkit-box-flex: 1;
  justify-content: center;
  align-items: center;
`

enum CallAnalyticsDependencyLoadingState {
  Waiting = 'waiting',
  Loading = 'loading',
  Loaded = 'loaded',
  Error = 'error',
}

export const CallAnalyticsPage = () => {
  const [selectedCall, setSelectedCall] = useState<CallLog | null>(null)
  const [selectedCallLoadingState, setSelectedCallLoadingState] =
    useState<CallAnalyticsDependencyLoadingState>(
      CallAnalyticsDependencyLoadingState.Waiting,
    )
  const [callTranscript, setCallTranscript] = useState<CallTranscript | null>(
    null,
  )
  const [callLogInstance, setCallLogInstance] =
    useState<CallLogInstance | null>(null)

  const { pbxId } = useUserState()
  const { data: transcribedCalls, isLoading: isTranscribedCallsLoading } =
    useGetTranscribedCalls(pbxId ?? 0)

  const {
    state: { isOpen: isListPanelOpen },
    togglePanelVisibility,
  } = useTogglePanel()
  useEffect(() => {
    if (!selectedCall && !isListPanelOpen) {
      togglePanelVisibility(true)
    }
  }, [])

  const { refetch: fetchCallTranscript } = useGetCallTranscript(
    pbxId ?? 0,
    selectedCall?.id ?? 0,
    (data) => {
      setCallTranscript(data)
    },
    (_) => {
      setSelectedCallLoadingState(CallAnalyticsDependencyLoadingState.Error)
    },
    {
      enabled: false,
    },
  )

  const { refetch: fetchCall } = useGetCall({
    onQuerySuccess: (data) => {
      setCallLogInstance(data as unknown as CallLogInstance)
    },
    onQueryError: (_) => {
      setSelectedCallLoadingState(CallAnalyticsDependencyLoadingState.Error)
    },
    options: {
      enabled: false,
    },
  })

  const handleSetSelectedCallLog = (call: CallLog | null) => {
    //don't set selected call log if it's already selected
    if (call && (selectedCall?.id ?? 0) === call.id) {
      return
    }

    setSelectedCall(call)
    togglePanelVisibility(false)
  }

  /** Loads new dependencies given a CallLog and sets loading state to loading */
  const handleLoadNewDependencies = (call: CallLog) => {
    setCallLogInstance(null)
    setCallTranscript(null)

    fetchCallTranscript()
    fetchCall(call.id)
    setSelectedCallLoadingState(CallAnalyticsDependencyLoadingState.Loading)
  }

  /** Loads new dependencies and sends a Google Analytics event when a new call log is selected */
  useEffect(() => {
    if (selectedCall) {
      handleLoadNewDependencies(selectedCall)
      window.gtag('event', 'transcription_selected')
    }
  }, [selectedCall])

  /** Resets selected call log when component unmounts */
  useEffect(() => {
    setSelectedCall(null)
  }, [])

  // Sets loading state to loaded when all dependencies are loaded
  useEffect(() => {
    if (
      selectedCallLoadingState !== CallAnalyticsDependencyLoadingState.Loading
    ) {
      return
    }

    if (callTranscript && callLogInstance) {
      setSelectedCallLoadingState(CallAnalyticsDependencyLoadingState.Loaded)
    }
  }, [callTranscript, callLogInstance])

  const getTranscriptViewWrapperChildren = (): {
    detailHeader: ReactNode
    detailView: ReactNode
  } => {
    let detailHeader: ReactNode
    let detailView: ReactNode
    switch (selectedCallLoadingState) {
      case CallAnalyticsDependencyLoadingState.Loaded:
        {
          detailHeader = null
          detailView = (
            <TranscriptionView
              onClosed={() => handleSetSelectedCallLog(null)}
              recordingUri={callLogInstance!.recording_url ?? ''}
              callTranscript={callTranscript!}
            />
          )
        }
        break
      case CallAnalyticsDependencyLoadingState.Loading:
        {
          detailHeader = null
          detailView = (
            <LoadingOrNoticeWrapper data-testid="transcript-view-loading">
              <LoadingSpinner />
            </LoadingOrNoticeWrapper>
          )
        }
        break
      case CallAnalyticsDependencyLoadingState.Error:
        {
          detailHeader = null
          detailView = (
            <LoadingOrNoticeWrapper>
              <Text fontStyle={FontStyle.MediumBold} color={light.dark900}>
                Uh oh - there doesn't seem to be a&nbsp;
                <span style={{ color: light.red300 }}>
                  transcript record
                </span>{' '}
                for this call.
              </Text>
            </LoadingOrNoticeWrapper>
          )
        }
        break
      default: {
        detailHeader = null
        detailView = <EmptyDetailView message="No Call Selected" />
      }
    }

    return { detailHeader, detailView }
  }

  const { detailHeader, detailView } = getTranscriptViewWrapperChildren()

  return (
    <ResponsivePage
      listPanel={
        isTranscribedCallsLoading ? (
          <LoadingOrNoticeWrapper data-testid="list-loading">
            <LoadingSpinner />
          </LoadingOrNoticeWrapper>
        ) : (
          <CallLogsList
            logEntries={transcribedCalls ?? []}
            onCallLogSelected={(callLog) => handleSetSelectedCallLog(callLog)}
          />
        )
      }
      detailPanel={detailView}
      header={detailHeader}
    ></ResponsivePage>
  )
}
